import { clsx } from 'clsx';
import { forwardRef } from 'react';
import { IJxtCheckbox } from './types';

const JxtCheckbox = forwardRef<HTMLInputElement, IJxtCheckbox>(
  (
    {
      name,
      label,
      value = false,
      indeterminate = false,
      disabled = false,
      multilineLabel = false,
      onChange,
      className,
    },
    ref
  ) => {
    if (ref && 'current' in ref && ref.current) {
      ref.current.indeterminate = indeterminate && !value;
    }

    return (
      <div className={className}>
        <div className="relative flex items-start">
          <div className="flex min-h-6 items-center group max-w-full">
            <label
              htmlFor={name}
              aria-label={name}
              className={clsx('relative flex cursor-pointer max-w-full', multilineLabel && 'items-start')}
            >
              {/* h-5 matches the label line height, so the box stays aligned with the first line */}
              <div className={clsx('relative flex items-center', multilineLabel && 'h-5 shrink-0')}>
                <input
                  ref={ref}
                  id={name}
                  name={name}
                  type="checkbox"
                  disabled={disabled}
                  onChange={onChange}
                  checked={value}
                  className={clsx(
                    'peer relative size-4 appearance-none rounded-sm border-2',
                    disabled
                      ? 'cursor-not-allowed border-neutral-40 checked:bg-neutral-40'
                      : 'cursor-pointer border-neutral-80 group-hover:border-primary-100 group-hover:bg-primary-10 checked:border-primary-100 checked:bg-primary-100 checked:group-hover:bg-primary-100',
                    indeterminate && !value && !disabled && 'border-primary-100 bg-primary-100'
                  )}
                />
                {/* Checkmark — visible when checked and not indeterminate */}
                <span
                  className={clsx(
                    'pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-white transition-opacity',
                    value && !indeterminate ? 'opacity-100' : 'opacity-0'
                  )}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    stroke="currentColor"
                    strokeWidth="1"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </span>
                {/* Dash — visible when indeterminate and not fully checked */}
                <span
                  className={clsx(
                    'pointer-events-none absolute top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 text-white transition-opacity',
                    indeterminate && !value ? 'opacity-100' : 'opacity-0'
                  )}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-3.5 w-3.5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </span>
              </div>
              {label && (
                <span
                  title={multilineLabel ? undefined : label}
                  className={clsx(
                    'text-body-s pl-2 select-none',
                    multilineLabel ? 'min-w-0 wrap-anywhere' : 'truncate',
                    disabled ? 'cursor-not-allowed text-neutral-40' : 'cursor-pointer text-neutral-120',
                    value || indeterminate ? 'font-semibold' : 'font-normal'
                  )}
                >
                  {label}
                </span>
              )}
            </label>
          </div>
        </div>
      </div>
    );
  }
);

JxtCheckbox.displayName = 'JxtCheckbox';

export default JxtCheckbox;
